/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun } from '@fortawesome/free-solid-svg-icons/faSun';
import { faMoon } from '@fortawesome/free-solid-svg-icons/faMoon';
import { faCircleHalfStroke } from '@fortawesome/free-solid-svg-icons/faCircleHalfStroke';

type Mode = 'light' | 'auto' | 'dark';

const MODES = [
  { value: 'light' as Mode, icon: faSun, label: { de: 'Helles Design', en: 'Light theme' } },
  { value: 'auto' as Mode, icon: faCircleHalfStroke, label: { de: 'Design wie im System', en: 'Theme as in the system' } },
  { value: 'dark' as Mode, icon: faMoon, label: { de: 'Dunkles Design', en: 'Dark theme' } },
];

const group = css({
  display: 'inline-flex',
  border: '1px solid var(--iti-border)',
  borderRadius: '999px',
  padding: '2px',
  gap: '2px',
  verticalAlign: 'middle',
});

const button = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '24px',
  padding: 0,
  border: 0,
  borderRadius: '999px',
  background: 'transparent',
  color: 'var(--iti-muted)',
  cursor: 'pointer',
  '& svg': { height: '13px' },
  '&:hover': { color: 'var(--iti-link)' },
});

const active = css({
  background: 'var(--iti-nav)',
  color: 'var(--iti-bg)',
  '&:hover': { color: 'var(--iti-bg)' },
});

function storedMode(): Mode {
  try {
    const mode = localStorage.getItem('theme');
    return mode === 'light' || mode === 'dark' ? mode : 'auto';
  } catch (e) {
    return 'auto';
  }
}

// Stores the choice; the script in base.html applies it (and follows the system for 'auto').
export function ThemeSwitch({ lang }: { lang: 'de' | 'en' }) {
  const [mode, setMode] = useState<Mode>(storedMode);
  // the choice can also change elsewhere, e.g. in the django CMS toolbar (see base.html)
  useEffect(() => {
    const follow = () => setMode(storedMode());
    window.addEventListener('iti-theme', follow);
    return () => window.removeEventListener('iti-theme', follow);
  }, []);

  const choose = (next: Mode) => {
    const old = localStorage.getItem('theme');
    localStorage.setItem('theme', next);
    window.dispatchEvent(new StorageEvent('storage', { key: 'theme', storageArea: localStorage, oldValue: old, newValue: next }));
    setMode(next);
  };

  return (
    <div role="radiogroup" aria-label={lang === 'en' ? 'Colour theme' : 'Farbschema'} css={group}>
      {MODES.map(({ value, icon, label }) => (
        <button key={value} type="button" role="radio" aria-checked={mode === value}
          title={label[lang]} aria-label={label[lang]} css={[button, mode === value && active]}
          onClick={() => choose(value)}>
          <FontAwesomeIcon icon={icon} />
        </button>
      ))}
    </div>
  );
}
